'use client';

import { cn } from '@/lib/utils';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import { ArrowRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AgendaCard, type AgendaCardData } from './agenda-card';
import styles from './agenda-section.module.scss';

type AgendaCarouselProps = {
  cards: AgendaCardData[];
  enableAutoplay?: boolean;
  controls?: boolean;
  autoscrollSpeed?: number;
};

export const AgendaCarousel = ({
  cards,
  enableAutoplay = false,
  controls = true,
  autoscrollSpeed = 4000,
}: AgendaCarouselProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progressKey, setProgressKey] = useState(0);
  const [progressActive, setProgressActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickStartRef = useRef<number>(0);
  const pauseElapsedRef = useRef<number>(0);

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    loop: true,
    slides: { perView: 1.15, spacing: 20 },
    breakpoints: {
      '(min-width: 640px)': { slides: { perView: 2.1, spacing: 20 } },
      '(min-width: 1024px)': { slides: { perView: 3.1, spacing: 20 } },
      '(min-width: 1280px)': { slides: { perView: 4, spacing: 20 } },
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
      setProgressKey((k) => k + 1);
    },
  });

  const scheduleNext = (delay?: number) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    tickStartRef.current = Date.now();
    timeoutRef.current = setTimeout(() => {
      instanceRef.current?.next();
      scheduleNext();
    }, delay ?? autoscrollSpeed);
  };

  const restartTimer = () => {
    if (!enableAutoplay) return;
    scheduleNext();
  };

  useEffect(() => {
    if (!enableAutoplay) return;
    setProgressActive(true);
    scheduleNext();
    return () => {
      setProgressActive(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableAutoplay, autoscrollSpeed]);

  const handleMouseEnter = () => {
    if (!enableAutoplay) return;
    pauseElapsedRef.current = Date.now() - tickStartRef.current;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (!enableAutoplay) return;
    setIsHovered(false);
    const remaining = Math.max(0, autoscrollSpeed - pauseElapsedRef.current);
    scheduleNext(remaining);
  };

  const goPrev = () => {
    instanceRef.current?.prev();
    restartTimer();
  };

  const goNext = () => {
    instanceRef.current?.next();
    restartTimer();
  };

  const goToSlide = (index: number) => {
    instanceRef.current?.moveToIdx(index);
    restartTimer();
  };

  return (
    <div className={styles.carousel_wrap}>
      <div className={styles.track_wrap}>
        {controls && (
          <button className={styles.arrow} onClick={goPrev} aria-label="Previous agenda item">
            <ArrowRight width={24} height={24} className={styles.arrow_prev_icon} aria-hidden />
          </button>
        )}

        <div
          ref={sliderRef}
          className="keen-slider"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {cards.map((card, i) => (
            <div
              key={`${card.id}-${i}`}
              className={cn('keen-slider__slide', styles.carousel_slide)}
            >
              <AgendaCard {...card} />
            </div>
          ))}
        </div>

        {controls && (
          <button className={styles.arrow} onClick={goNext} aria-label="Next agenda item">
            <ArrowRight width={24} height={24} aria-hidden />
          </button>
        )}
      </div>

      {controls && (
        <div className={styles.dots}>
          {cards.map((_, i) => (
            <button
              key={i}
              className={cn(styles.dot, i === currentSlide && styles.dot_active)}
              onClick={() => goToSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
            >
              {progressActive && i === currentSlide && (
                <span
                  key={progressKey}
                  className={styles.dot_progress}
                  style={
                    {
                      '--progress-duration': `${autoscrollSpeed}ms`,
                      animationPlayState: isHovered ? 'paused' : 'running',
                    } as React.CSSProperties
                  }
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
